// src/components/layout/POSLayout.jsx
// POS Layout component optimized for touch interface and cashier workflow

import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { cn } from '../../utils/cn'
import PropTypes from 'prop-types'
import {
  LogOut,
  User,
  Coffee,
  Settings,
  ChevronDown,
  Calendar,
  Building,
  ArrowLeftRight,
  Truck,
  Car,
  X,
  RefreshCw,
  Maximize,
  Minimize,
  ChefHat
} from 'lucide-react'
import { format } from 'date-fns'
import BranchService from '../../services/branchService'
import OrderService from '../../services/orderService'
import toast from 'react-hot-toast'
import { DEFAULT_CUSTOMERS } from '../../config/constants'
import { PERMISSIONS, hasPermission } from '../../config/roles'
import POSNavigation from '../pos/POSNavigation'
import CategorySidebar from '../pos/CategorySidebar'
import POSBottomNavigation from '../pos/POSBottomNavigation'
import POSProductDisplay from '../pos/POSProductDisplay'
import CurrentOrder from '../pos/CurrentOrder'
import BranchSelectionModal from '../modals/BranchSelectionModal'
import PromotionsModal from '../modals/PromotionsModal'
import OpenShiftModal from '../modals/OpenShiftModal'
import CloseShiftModal from '../modals/CloseShiftModal'
import BaristaOrdersModal from '../modals/BaristaOrdersModal'
import DeliveryProviderPopup from '../modals/DeliveryProviderPopup'
import ShiftService from '../../services/shiftService'
import webPromotionService from '../../services/webPromotionService'

export const POSLayout = ({ user, onLogout }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [branchInfo, setBranchInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showBranchSelector, setShowBranchSelector] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  
  // Category state
  const [selectedCategory, setSelectedCategory] = useState(null)
  
  // Order state for bottom navigation
  const [orderType, setOrderType] = useState('dine-in')
  const [cartItems, setCartItems] = useState([])
  const [currentOrder, setCurrentOrder] = useState(null)
  const [currentOrderId, setCurrentOrderId] = useState(null) // Store current order ID for patching
  const [selectedTable, setSelectedTable] = useState(null) // Add table state
  const [orderCalculation, setOrderCalculation] = useState(null) // Add order calculation state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false) // Track payment modal state
  const [isHoldOrdersModalOpen, setIsHoldOrdersModalOpen] = useState(false) // Track hold orders modal state
  const [isPromotionsModalOpen, setIsPromotionsModalOpen] = useState(false) // Track promotions modal state
  const [isBaristaModalOpen, setIsBaristaModalOpen] = useState(false) // Track barista orders modal state
  const [isVIPRoomsModalOpen, setIsVIPRoomsModalOpen] = useState(false) // Track VIP rooms modal state
  const [currentCustomerId, setCurrentCustomerId] = useState(DEFAULT_CUSTOMERS.WALKING_CUSTOMER) // Customer state - hardcoded walking customer ID
  
  // Lifted customer and web promotions state
  const [currentCustomer, setCurrentCustomer] = useState(null)
  const [webPromotions, setWebPromotions] = useState([])
  const [selectedWebPromo, setSelectedWebPromo] = useState(null)
  
  // Wrapper for setCurrentCustomerId with logging
  const handleCustomerIdChange = (customerId) => {
    console.log('📥 [POSLayout] handleCustomerIdChange called with:', customerId);
    setCurrentCustomerId(customerId);
  };
  
  // Debug: Track customer ID changes
  useEffect(() => {
    console.log('🔄 [POSLayout] currentCustomerId state changed to:', currentCustomerId);
  }, [currentCustomerId]);
  
  // Delivery provider state
  const [deliveryProvider, setDeliveryProvider] = useState(null) // 'pickme' or 'uber'
  const [showDeliveryProviderPopup, setShowDeliveryProviderPopup] = useState(false)
  const [deliveryReference, setDeliveryReference] = useState('') // Store last digit of order ID for delivery
  
  // Shift management state
  const [shiftStatus, setShiftStatus] = useState('closed') // 'closed', 'open'
  const [currentShift, setCurrentShift] = useState(null)
  const [isOpenShiftModalOpen, setIsOpenShiftModalOpen] = useState(false)
  const [isCloseShiftModalOpen, setIsCloseShiftModalOpen] = useState(false)
  const [shiftLoading, setShiftLoading] = useState(false)
  
  // Ref for POSProductDisplay to refresh products
  const posProductDisplayRef = useRef(null)
  
  const [loginTime] = useState(() => {
    // Get persistent login time or set current time
    const storedLoginTime = localStorage.getItem('tezlaa-login-time')
    if (storedLoginTime) {
      return new Date(storedLoginTime)
    } else {
      const now = new Date()
      localStorage.setItem('tezlaa-login-time', now.toISOString())
      return now
    }
  })

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Fetch branch information based on user data
  useEffect(() => {
    const fetchBranchData = async () => {
      try {
        setLoading(true)
        
        // Use the user data that's already available from localStorage/login
        if (user?.branchId) {
          console.log('🏢 Fetching branch info for branchId:', user.branchId)
          const branchResponse = await BranchService.getBranchById(user.branchId)
          if (branchResponse?.success) {
            // Handle different response structures
            const branchData = branchResponse.data?.branch || branchResponse.data
            setBranchInfo(branchData)
            console.log('✅ Branch info fetched:', branchData)
          } else {
            console.warn('⚠️ Branch fetch failed:', branchResponse)
          }
        } else {
          console.log('ℹ️ No branchId found in user data')
        }
      } catch (error) {
        console.error('❌ Error fetching branch data:', error)
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      fetchBranchData()
    }
  }, [user])

  // Check for existing active shift on component mount
  useEffect(() => {
    const checkActiveShift = async () => {
      try {
        if (user?.branchId) {
          console.log('🔍 Checking for active shift...')
          const response = await ShiftService.getActiveShift(user.branchId)
          
          if (response?.success && response?.data) {
            // Handle the API response structure: { success: true, data: { shiftCashBalances: [...], totalResults: 1 } }
            let activeShift = null
            
            if (response.data.shiftCashBalances && Array.isArray(response.data.shiftCashBalances)) {
              activeShift = response.data.shiftCashBalances[0]
            } else if (Array.isArray(response.data)) {
              activeShift = response.data[0]
            } else if (response.data._id) {
              activeShift = response.data
            }
            
            if (activeShift) {
              setCurrentShift(activeShift)
              setShiftStatus('open')
              console.log('✅ Active shift found:', activeShift)
            } else {
              setShiftStatus('closed')
              console.log('ℹ️ No active shift found in response')
            }
          } else {
            setShiftStatus('closed')
            console.log('ℹ️ No active shift found')
          }
        }
      } catch (error) {
        console.error('❌ Error checking active shift:', error)
        setShiftStatus('closed')
      }
    }

    if (user?.branchId) {
      checkActiveShift()
    }
  }, [user?.branchId])

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category)
  }

  // Handle order type change
  const handleOrderTypeChange = (type) => {
    // If switching to delivery, show provider selection popup
    if (type === 'delivery') {
      setShowDeliveryProviderPopup(true)
      return
    }
    
    // Clear delivery provider if not delivery
    if (type !== 'delivery') {
      setDeliveryProvider(null)
      setDeliveryReference('')
    }
    
    setOrderType(type)
    console.log('Order type changed to:', type)
    
    // Clear selected table if switching to takeaway or delivery
    if (type === 'takeaway' || type === 'delivery') {
      if (selectedTable) {
        console.log('🪑 Clearing table selection for', type, 'order')
        setSelectedTable(null)
      }
    }
  }

  // Handle delivery provider selection
  const handleDeliveryProviderSelect = (provider, reference = '') => {
    setDeliveryProvider(provider)
    setDeliveryReference(reference)
    setOrderType('delivery')
    setShowDeliveryProviderPopup(false)
    console.log('Delivery provider selected:', provider, 'Reference:', reference)
  }

  // Handle bottom navigation actions
  const handleHoldOrders = () => {
    console.log('Hold orders action')
    // Implement hold orders logic
  }

  const handleTables = () => {
    console.log('Tables action')
    // This will open the table selection modal in POSBottomNavigation
  }

  // Handle table selection
  const handleTableSelect = (table) => {
    setSelectedTable(table)
    console.log('✅ Table selected:', table.number, 'for', table.capacity, 'people')
    
    // Update order type to dine-in if a table is selected
    if (table) {
      setOrderType('dine-in')
    }
  }

  // Handle table clear
  const handleTableClear = () => {
    setSelectedTable(null)
    console.log('❌ Table selection cleared')
  }

  // Handle shift management
  const handleShiftAction = () => {
    if (shiftStatus === 'closed') {
      setIsOpenShiftModalOpen(true)
    } else {
      setIsCloseShiftModalOpen(true)
    }
  }

  const handleOpenShift = async (openBalance) => {
    try {
      setShiftLoading(true)
      
      if (!user?.branchId) {
        toast.error('Branch information is missing')
        return
      }

      const data = {
        openBalance: openBalance,
        branchId: user.branchId
      }

      const response = await ShiftService.openShift(data)
      
      if (response?.success) {
        setCurrentShift(response.data.shiftCashBalance)
        setShiftStatus('open')
        setIsOpenShiftModalOpen(false)
        toast.success('Shift opened successfully')
        console.log('✅ Shift opened:', response.data.shiftCashBalance)
      } else {
        toast.error('Failed to open shift')
      }
    } catch (error) {
      console.error('❌ Error opening shift:', error)
      toast.error('Failed to open shift. Please try again.')
    } finally {
      setShiftLoading(false)
    }
  }

  const handleCloseShift = async (closeBalance) => {
    try {
      console.log('🔄 Starting close shift handler:', { closeBalance, currentShift })
      setShiftLoading(true)
      
      if (!currentShift?._id) {
        console.error('❌ No active shift found:', currentShift)
        toast.error('No active shift found')
        return
      }

      const data = {
        closeBalance: closeBalance
      }

      console.log('📞 Calling ShiftService.closeShift:', { shiftId: currentShift._id, data })
      const response = await ShiftService.closeShift(currentShift._id, data)
      console.log('📨 Received close shift response:', response)
      
      if (response?.success) {
        setCurrentShift(null)
        setShiftStatus('closed')
        setIsCloseShiftModalOpen(false)
        toast.success('Shift closed successfully')
        console.log('✅ Shift closed:', response.data?.shiftCashBalance || response.data)
        
        // Clear login time and logout after closing shift
        localStorage.removeItem('tezlaa-login-time')
        setTimeout(() => {
          onLogout()
        }, 1000) // Small delay to show success message
      } else {
        toast.error('Failed to close shift')
      }
    } catch (error) {
      console.error('❌ Error closing shift:', error)
      toast.error('Failed to close shift. Please try again.')
    } finally {
      setShiftLoading(false)
    }
  }

  const handlePayLater = async () => {
    try {
      console.log('💰 Processing Pay Later...')
      
      // Validate that we have items to order
      if (!cartItems || cartItems.length === 0) {
        toast.error('No items in cart to create order')
        return
      }

      // Validate required data
      if (!branchInfo?._id) {
        toast.error('Branch information is missing. Please try again.')
        return
      }

      // Convert order type to API format
      const getApiOrderType = (orderType) => {
        switch (orderType) {
          case 'dine-in':
            return 'dine_in';
          case 'takeaway':
            return 'take_away';
          case 'delivery':
            return 'delivery';
          default:
            return orderType.replace('-', '_'); // fallback for any other hyphenated types
        }
      };

      // Prepare order data
      // forcePunch:true — Pay Later must never be blocked by stock levels; the cashier
      // is explicitly choosing to defer payment, so we record a deficit if needed.
      const orderData = {
        branchId: branchInfo._id,
        customerId: currentCustomerId || DEFAULT_CUSTOMERS.WALKING_CUSTOMER,
        orderType: getApiOrderType(orderType),
        orderDate: new Date().toISOString(), // Explicitly set orderDate in UTC
        forcePunch: true,
        orderItems: cartItems
          .map(item => ({
            productId: item.product?._id || item.productId || item.id,
            quantity: parseInt(item.quantity) || 1
          }))
          .filter(item => item.productId && item.quantity && item.quantity > 0)
      }

      // Add table for dine-in orders
      if (orderType === 'dine-in' && selectedTable?._id) {
        orderData.table = selectedTable._id
      }

      // Add delivery provider for delivery orders
      if (orderType === 'delivery' && deliveryProvider) {
        orderData.deliveryProvider = deliveryProvider
        if (deliveryReference) {
          orderData.deliveryReference = deliveryReference
        }
      }

      let orderResponse

      // Check if we should create new order or update existing one
      if (currentOrderId) {
        console.log('📝 Updating existing pending order with ID:', currentOrderId)
        
        orderResponse = await toast.promise(
          OrderService.updateOrder(currentOrderId, orderData),
          {
            loading: `Updating pending order ${currentOrderId.slice(-6)}...`,
            success: (response) => `Order ${response.data?.order?.orderNumber || currentOrderId.slice(-6)} updated for later payment!`,
            error: 'Failed to update pending order'
          }
        )
      } else {
        console.log('📝 Creating new pending order')
        
        orderResponse = await toast.promise(
          OrderService.createOrder(orderData),
          {
            loading: 'Creating pending order...',
            success: (response) => `Order ${response.data?.order?.orderNumber || 'created'} saved for later payment!`,
            error: 'Failed to create pending order'
          }
        )
      }

      if (orderResponse.success && orderResponse.data?.order) {
        const createdOrder = orderResponse.data.order
        console.log('✅ Pending order processed successfully:', createdOrder)
        
        // Clear the cart after successful order creation/update
        setCartItems([])
        setCurrentOrder(null)
        setOrderCalculation(null)
        setSelectedTable(null)
        setCurrentOrderId(null) // Clear order ID for Pay Later since it's completed
        setCurrentCustomerId(DEFAULT_CUSTOMERS.WALKING_CUSTOMER)
        setDeliveryProvider(null) // Clear delivery provider
        setDeliveryReference('') // Clear delivery reference
        setOrderType('dine-in') // Reset to dine-in
        
        // Refresh products to update stock levels after pay later
        if (posProductDisplayRef.current?.refreshProducts) {
          console.log('🔄 Refreshing products after pay later order completion...')
          posProductDisplayRef.current.refreshProducts()
        }
        
      } else {
        throw new Error(orderResponse.message || 'Failed to process order')
      }
      
    } catch (error) {
      console.error('❌ Error creating pending order:', error)
      toast.error(`Failed to save order: ${error.message}`)
    }
  }

  const handleDiscount = () => {
    console.log('Discount action')
    // Implement discount logic
  }

  const handleCustomers = () => {
    console.log('Customers action')
    // Implement customers logic
  }

  // Handle customer selection from bottom navigation
  const handleCustomerSelect = (customer) => {
    console.log('👤 [POSLayout] Customer selected from bottom navigation:', customer);
    console.log('🔍 [POSLayout] Customer object keys:', customer ? Object.keys(customer) : 'null/undefined');
    console.log('🔍 [POSLayout] Customer._id:', customer?._id);
    console.log('🔍 [POSLayout] Current customerId state before update:', currentCustomerId);
    
    if (customer?._id) {
      console.log('✅ [POSLayout] Valid customer - updating state with ID:', customer._id);
      setCurrentCustomerId(customer._id);
      setCurrentCustomer(customer);
      console.log('✅ [POSLayout] Customer ID set to:', customer._id, '| Name:', customer.name || 'Unknown');
    } else {
      console.error('❌ [POSLayout] Invalid customer object - missing _id:', customer);
    }
  }

  // Handle refresh functionality
  const handleRefresh = () => {
    console.log('Refreshing POS...')
    toast.success('Refreshing POS...')
    window.location.reload()
  }

  // Handle fullscreen toggle
  const handleFullscreenToggle = async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        await document.documentElement.requestFullscreen()
        setIsFullscreen(true)
        toast.success('Entered fullscreen mode')
      } else {
        // Exit fullscreen
        await document.exitFullscreen()
        setIsFullscreen(false)
        toast.success('Exited fullscreen mode')
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error)
      toast.error('Failed to toggle fullscreen')
    }
  }

  // Listen for fullscreen changes (user can also exit with Esc key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const handleClearAll = () => {
    const itemCount = cartItems?.length || 0
    setCartItems([])
    setCurrentOrder(null)
    setOrderCalculation(null)
    setCurrentOrderId(null) // Clear current order ID
    setCurrentCustomerId(DEFAULT_CUSTOMERS.WALKING_CUSTOMER) // Reset to walking customer
    setDeliveryProvider(null) // Clear delivery provider
    setDeliveryReference('') // Clear delivery reference
    setOrderType('dine-in') // Reset to dine-in
    setSelectedTable(null) // Clear selected table
    
    // Reset web promotion states
    setWebPromotions([])
    setSelectedWebPromo(null)
    setCurrentCustomer(null)

    // Show success toast
    toast.success(`All items cleared! (${itemCount} items removed)`)
    console.log('✅ All items cleared - cart, order, customer, table, and provider reset')
  }

  // Handle Pay button click - create order first, then open payment modal
  const handlePayButtonClick = async () => {
    try {
      console.log('💳 Pay button clicked - creating order first...')

      // Validate that we have items to order
      if (!cartItems || cartItems.length === 0) {
        toast.error('No items in cart to process payment for')
        return
      }

      // Debug: Log cart items and their quantities
      console.log('🔍 Cart items before processing:', cartItems)
      console.log('🔍 Cart item quantities:', cartItems.map(item => ({ 
        id: item.id || item.productId, 
        name: item.name, 
        quantity: item.quantity,
        hasProduct: !!item.product
      })))

      // Validate required data
      if (!branchInfo?._id) {
        toast.error('Branch information is missing. Please try again.')
        return
      }

      // Convert order type to API format  
      const getApiOrderType = (orderType) => {
        switch (orderType) {
          case 'dine-in':
            return 'dine_in';
          case 'takeaway':
            return 'take_away';
          case 'delivery':
            return 'delivery';
          default:
            return orderType.replace('-', '_'); // fallback for any other hyphenated types
        }
      };

      // Prepare order data
      const orderData = {
        branchId: branchInfo._id,
        customerId: currentCustomerId || DEFAULT_CUSTOMERS.WALKING_CUSTOMER,
        orderType: getApiOrderType(orderType),
        orderDate: new Date().toISOString(), // Explicitly set orderDate in UTC
        // Include the selected web promotion if applied
        ...(selectedWebPromo && {
          webPromotion: {
            webCustomerPromotionId: selectedWebPromo._id,
            title: selectedWebPromo.promotionSnapshot.title,
            reward: selectedWebPromo.promotionSnapshot.reward,
            discountValue: selectedWebPromo.promotionSnapshot.discountValue,
            freeProductId: selectedWebPromo.promotionSnapshot.freeProductId,
            freeProductName: selectedWebPromo.promotionSnapshot.freeProductName,
            minOrderValue: selectedWebPromo.promotionSnapshot.minOrderValue
          }
        }),
        orderItems: cartItems
          .map(item => {
            // Enhanced productId extraction with multiple fallbacks
            let productId = null
            
            // Try different ways to get productId
            if (item.product?._id) {
              productId = item.product._id
            } else if (item.productId) {
              productId = item.productId
            } else if (item.id) {
              productId = item.id
            } else if (item._id) {
              productId = item._id
            }
            
            const quantity = parseInt(item.quantity) || 1
            
            // Debug log each item
            console.log('🔍 Processing cart item:', { 
              productId, 
              quantity, 
              originalQuantity: item.quantity,
              itemStructure: {
                id: item.id,
                productId: item.productId,
                productObjectId: item.product?._id,
                _id: item._id
              },
              item: item
            })
            
            return {
              productId,
              quantity
            }
          })
          .filter(item => {
            const isValid = item.productId && item.quantity && item.quantity > 0
            if (!isValid) {
              console.warn('⚠️ Filtering out invalid item:', item)
            }
            return isValid
          })
      }

      // Debug: Log final order data
      console.log('📝 Final order data:', orderData)
      console.log('📝 Order items details:', orderData.orderItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        quantityType: typeof item.quantity,
        isValidQuantity: item.quantity > 0
      })))

      // Add table for dine-in orders
      if (orderType === 'dine-in' && selectedTable?._id) {
        orderData.table = selectedTable._id
      }

      // Add delivery provider for delivery orders
      if (orderType === 'delivery' && deliveryProvider) {
        orderData.deliveryProvider = deliveryProvider
        if (deliveryReference) {
          orderData.deliveryReference = deliveryReference
        }
      }

      let orderResponse

      // Check if we should create new order or update existing one
      if (currentOrderId) {
        console.log('📝 Updating existing order with ID:', currentOrderId)
        
        // For order updates, ensure we're sending the correct data structure
        // Some backends expect different formats for updates vs creates
        const updateOrderData = {
          ...orderData,
          // Ensure orderItems are properly structured for update
          orderItems: orderData.orderItems.map(item => ({
            ...item,
            // Ensure quantity is a proper number for backend validation
            quantity: Number(item.quantity)
          }))
        }
        
        console.log('📝 Update payload:', JSON.stringify(updateOrderData, null, 2))
        
        orderResponse = await toast.promise(
          OrderService.updateOrder(currentOrderId, updateOrderData),
          {
            loading: `Updating order ${currentOrderId.slice(-6)}...`,
            success: (response) => `Order ${response.data?.order?.orderNumber || currentOrderId.slice(-6)} updated!`,
            error: 'Failed to update order'
          }
        )
      } else {
        console.log('📝 Creating new order')
        
        orderResponse = await toast.promise(
          OrderService.createOrder(orderData),
          {
            loading: 'Creating new order...',
            success: (response) => `Order ${response.data?.order?.orderNumber || 'created'} created!`,
            error: 'Failed to create order'
          }
        )
      }

      if (!orderResponse.success || !orderResponse.data?.order) {
        throw new Error(orderResponse.message || 'Failed to create order')
      }

      const apiOrder = orderResponse.data.order
      console.log('✅ Order created successfully:', apiOrder)

      // Store order ID for future updates
      if (apiOrder._id) {
        setCurrentOrderId(apiOrder._id)
      }

      // Now open the payment modal for payment processing
      console.log('💳 Opening payment modal for order:', apiOrder.orderNumber)
      setIsPaymentModalOpen(true)

    } catch (error) {
      console.error('❌ Error creating order:', error)
      toast.error(`Failed to create order: ${error.message}`)
    }
  }

  // Handle payment success
  const handlePaymentSuccess = (result) => {
    console.log('💰 Payment completed successfully:', result)

    // Claim promotion on website if applied
    if (selectedWebPromo && currentCustomer?.mobileNumber) {
      console.log('🎉 Claiming web promotion at checkout completion:', selectedWebPromo._id)
      webPromotionService.claimWebPromotion(
        currentCustomer.mobileNumber,
        selectedWebPromo._id
      ).then((res) => {
        if (res.success) {
          console.log('✅ Promotion claimed successfully on website.')
        } else {
          console.warn('⚠️ Failed to claim promotion on website:', res.error)
        }
      }).catch((err) => {
        console.error('❌ Error claiming web promotion:', err)
      })
    }

    // Clear the cart and order after successful payment
    setCartItems([])
    setCurrentOrder(null)
    setOrderCalculation(null)
    setSelectedTable(null)
    setCurrentOrderId(null) // Clear current order ID after successful payment
    setCurrentCustomerId(DEFAULT_CUSTOMERS.WALKING_CUSTOMER) // Reset to walking customer for next order
    setDeliveryProvider(null) // Clear delivery provider
    setDeliveryReference('') // Clear delivery reference
    setOrderType('dine-in') // Reset to dine-in
    setWebPromotions([])
    setSelectedWebPromo(null)
    setCurrentCustomer(null)
    
    // Refresh products to update stock levels
    if (posProductDisplayRef.current?.refreshProducts) {
      console.log('🔄 Refreshing products after payment completion...')
      posProductDisplayRef.current.refreshProducts()
    }
    
    // You might want to show a success message or receipt here
    // For now, we'll just log the success
    console.log('✅ Order created and paid:', result.order.orderNumber)
  }

  // Handle loading an order from Hold Orders
  const handleOrderLoad = (cartData) => {
    try {
      console.log('📋 Loading order into POS:', cartData)
      
      // Set the order ID to ensure future changes patch this order
      if (cartData.orderId || cartData._id) {
        const orderId = cartData.orderId || cartData._id
        setCurrentOrderId(orderId)
        console.log('📋 Order ID set for patching:', orderId)
      }
      
      // Set cart items
      if (cartData.cartItems && cartData.cartItems.length > 0) {
        // Debug: Log cart items before setting
        console.log('📋 Cart items from loaded order:', cartData.cartItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          originalQuantity: item.originalItem?.quantity,
          hasProduct: !!item.product,
          productId: item.productId || item.product?._id || item.id
        })))
        
        // Validate and clean cart items before setting
        const validCartItems = cartData.cartItems.filter(item => {
          const hasValidId = !!(item.id || item.productId || item.product?._id)
          const hasValidQuantity = item.quantity && parseInt(item.quantity) > 0
          const hasValidPrice = item.price && parseFloat(item.price) > 0
          
          if (!hasValidId) {
            console.warn('⚠️ Cart item missing valid ID:', item)
            return false
          }
          if (!hasValidQuantity) {
            console.warn('⚠️ Cart item has invalid quantity:', item.quantity, 'for item:', item.name)
            return false
          }
          if (!hasValidPrice) {
            console.warn('⚠️ Cart item has invalid price:', item.price, 'for item:', item.name)
            return false
          }
          
          return true
        }).map(item => {
          // Normalize the cart item structure to match what handleAddToCart expects
          const productId = item.productId || item.product?._id || item.id;
          const productData = item.product || item;
          
          return {
            id: productId, // Use productId as the main ID
            name: item.name || productData.name,
            price: parseFloat(item.price) || 0,
            quantity: parseInt(item.quantity) || 1,
            product: productData,
            productId: productId, // Ensure productId is set
            category: item.category || productData.category,
            sku: item.sku || productData.sku,
            type: item.type || productData.type,
            currentStock: item.currentStock || productData.currentStock,
            originalItem: item.originalItem, // Keep original reference
            addedAt: item.addedAt || new Date() // Add timestamp if not present
          };
        })
        
        console.log('📋 Validated cart items DETAILED:', validCartItems.map((item, index) => ({
          index,
          id: item.id,
          productId: item.productId,
          'product._id': item.product?._id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          originalId: cartData.cartItems[index]?.id,
          originalProductId: cartData.cartItems[index]?.productId
        })))
        
        console.log('📋 Setting normalized cart items. Count:', validCartItems.length)
        setCartItems(validCartItems)
        console.log('✅ Cart items set successfully. Total items:', validCartItems.length)
      } else {
        console.warn('⚠️ No cart items in loaded order data')
      }
      
      // Set order type (normalize format from API to UI)
      if (cartData.orderType) {
        const getUIOrderType = (apiOrderType) => {
          switch (apiOrderType) {
            case 'dine_in':
              return 'dine-in';
            case 'take_away':
              return 'takeaway';
            case 'delivery':
              return 'delivery';
            default:
              return apiOrderType.replace('_', '-'); // fallback for any other underscore types
          }
        };
        
        const normalizedOrderType = getUIOrderType(cartData.orderType);
        setOrderType(normalizedOrderType)
        console.log('🔄 Order type set to:', normalizedOrderType)
      }
      
      // Set customer if available
      if (cartData.customer && cartData.customer._id) {
        setCurrentCustomerId(cartData.customer._id)
        setCurrentCustomer(cartData.customer)
      }
      
      // Set table if it's a dine-in order (check both formats)
      if (cartData.table && (cartData.orderType === 'dine-in' || cartData.orderType === 'dine_in')) {
        setSelectedTable(cartData.table)
        console.log('🪑 Table set to:', cartData.table.name || cartData.table._id)
      }
      
      // Show comprehensive success message
      const orderId = cartData.orderId || cartData._id
      const getDisplayOrderType = (apiOrderType) => {
        switch (apiOrderType) {
          case 'dine_in':
            return 'DINE-IN';
          case 'take_away':
            return 'TAKEAWAY';
          case 'delivery':
            return 'DELIVERY';
          default:
            return apiOrderType.replace('_', '-').toUpperCase();
        }
      };
      const orderType = cartData.orderType ? getDisplayOrderType(cartData.orderType) : 'UNKNOWN'
      const tableInfo = cartData.table ? ` | Table: ${cartData.table.name || cartData.table._id}` : ''
      toast.success(`Pending order loaded! ${orderType} | ID: ${orderId ? orderId.slice(-6) : 'unknown'}${tableInfo}`)
      
      console.log('✅ Order loaded successfully into POS with ID:', cartData.orderId || cartData._id)
    } catch (error) {
      console.error('❌ Error loading order into POS:', error)
    }
  }

  // Branch switching handlers for admin/superadmin
  const handleBranchSwitch = () => {
    setShowBranchSelector(true)
  }

  // Handle barista orders modal
  const handleBaristaOrders = () => {
    setIsBaristaModalOpen(true)
  }

  const handleBranchSelect = (selectedBranch) => {
    // Update localStorage and user data
    const updatedUser = {
      ...user,
      branchId: selectedBranch._id,
      selectedBranch: selectedBranch,
      branch: selectedBranch
    }
    
    localStorage.setItem('tezlaa-user', JSON.stringify(updatedUser))
    localStorage.setItem('tezlaa-selected-branch', JSON.stringify(selectedBranch))
    
    // Refresh the page to load the new branch data
    window.location.reload()
  }

  // Check if we're on a POS page that should show categories and bottom nav
  const showCategories = location.pathname.includes('/pos') && 
                          !location.pathname.includes('/pos/settings') &&
                          !location.pathname.includes('/pos/reports') &&
                          !location.pathname.includes('/pos/tables') &&
                          !location.pathname.includes('/pos/customers')
  
  const showBottomNav = location.pathname.includes('/pos') && 
                        !location.pathname.includes('/pos/settings') &&
                        !location.pathname.includes('/pos/reports')

  // Check if we should show the main POS interface (product display)
  const showPOSInterface = location.pathname === '/pos' || location.pathname === '/pos/'

  const handleNavigation = (path) => {
    navigate(path)
  }

  const currentDate = format(currentTime, 'M/d/yyyy')
  const currentTimeString = format(currentTime, 'h:mm:ss a')

  // Use user data directly from props (already from localStorage/login)
  const userName = user?.username || user?.name || 'Cashier'
  const userRole = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Cashier'
  const branchName = branchInfo?.name || user?.branch?.name || user?.branchName || 'Branch'
  
  // Check if current user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Top Header Bar - Orange branded header */}
      <header className="bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg">
        <div className="flex items-center justify-between px-6 h-16">
          {/* Left Section - Logo and Branch Info */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              {/* Logo - Increased size */}
              <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center p-2">
                <img
                  src="/logo.png"
                  alt="TEZLAA Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    // Fallback to Coffee icon if logo fails to load
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
                <Coffee className="h-10 w-10 hidden" />
              </div>
              <div>
                <h1 className="text-lg font-bold">{branchName}</h1>
                <p className="text-orange-100 text-sm">{userRole}</p>
              </div>
            </div>
          </div>

          {/* Center Section - Date and Time with Calendar Icon */}
          <div className="flex items-center space-x-2 text-center">
            <Calendar className="h-5 w-5 text-orange-100" />
            <div className="text-sm font-medium text-orange-100">
              {currentDate}, {currentTimeString}
            </div>
          </div>

          {/* Right Section - User Actions */}
          <div className="flex items-center space-x-4">
            {/* Refresh Button */}
            <button 
              onClick={handleRefresh}
              className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition-colors flex items-center"
              title="Refresh POS"
            >
              <RefreshCw className="h-5 w-5" />
            </button>

            {/* Fullscreen Toggle Button */}
            <button 
              onClick={handleFullscreenToggle}
              className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition-colors flex items-center"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize className="h-5 w-5" />
              ) : (
                <Maximize className="h-5 w-5" />
              )}
            </button>

            {/* Barista Orders Button */}
            <button 
              onClick={handleBaristaOrders}
              className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
              title="View Barista Orders"
            >
              <ChefHat className="h-4 w-4" />
              <span className="hidden sm:inline">Barista</span>
            </button>

            {/* Branch Switcher for Admin/SuperAdmin/Manager */}
            {(isAdmin || user?.role === 'manager') && (
              <button 
                onClick={handleBranchSwitch}
                className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                title="Switch Branch"
              >
                <Building className="h-4 w-4" />
                <span className="hidden sm:inline">Switch Branch</span>
              </button>
            )}

            {/* Open/Close Shift Button - Only for Cashier and Barista roles */}
            {(user?.role === 'cashier' || user?.role === 'barista') && (
              <button 
                onClick={handleShiftAction}
                disabled={shiftLoading}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  shiftStatus === 'closed' 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {shiftLoading 
                  ? (shiftStatus === 'closed' ? 'Opening...' : 'Closing...') 
                  : (shiftStatus === 'closed' ? 'Open Shift' : 'Close Shift')
                }
              </button>
            )}

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium hidden sm:block">
                  {userName}
                </span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border py-1 z-50">
                  <div className="px-4 py-3 border-b">
                    <p className="text-sm font-medium text-gray-900">
                      {userName}
                    </p>
                    <p className="text-xs text-gray-500">{userRole} • {branchName}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Logged in: {format(loginTime, 'MMM d, h:mm a')}
                    </p>
                    {user?.email && (
                      <p className="text-xs text-gray-400">{user.email}</p>
                    )}
                  </div>
                  {/* Show Admin Panel button for admin and manager users */}
                  {(isAdmin || user?.role === 'manager') && (
                    <button
                      onClick={() => {
                        navigate('/admin')
                        setUserMenuOpen(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Admin Panel
                    </button>
                  )}
                  {(isAdmin || user?.role === 'manager') && (
                    <button
                      onClick={() => {
                        navigate('/barista')
                        setUserMenuOpen(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Barista Screen
                    </button>
                  )}
                  <button
                    onClick={() => {
                      localStorage.removeItem('tezlaa-login-time')
                      onLogout()
                      setUserMenuOpen(false)
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Page Content - Flex layout with current order sidebar */}
      <div className="flex-1 overflow-hidden bg-gray-100 relative flex flex-col">
        <main className="flex-1 overflow-hidden relative flex">
          {/* Left Section - Main POS Interface */}
          <div className="flex-1 relative">
          {/* POS Navigation - Fixed top-left corner */}
          {!showBranchSelector && !isPaymentModalOpen && !isHoldOrdersModalOpen && !isBaristaModalOpen && !isVIPRoomsModalOpen && (
            <POSNavigation 
              className="absolute top-4 left-4 z-[9999]" 
              branchId={user?.branchId || branchInfo?._id}
              branchInfo={branchInfo}
            />
          )}
          
          {/* Category Buttons - Simple left side below navigation with proper spacing */}
          {showCategories && !showBranchSelector && !isPaymentModalOpen && !isHoldOrdersModalOpen && !isBaristaModalOpen && !isVIPRoomsModalOpen && (
              <div className={`absolute top-44 left-4 w-36 z-[9998] ${showBottomNav ? 'bottom-36' : 'bottom-4'}`}>
                <CategorySidebar
                  selectedCategory={selectedCategory}
                  onCategorySelect={handleCategorySelect}
                />
              </div>
            )}
            
            {/* Page Content */}
            <div 
              className="h-full"
              data-selected-category={selectedCategory?._id || 'all'}
              data-category-name={selectedCategory?.name || 'All Products'}
            >
              {/* Show POS Product Display on main POS route */}
              {showPOSInterface ? (
                <POSProductDisplay 
                  ref={posProductDisplayRef}
                  selectedBranchId={user?.branchId}
                  selectedCategory={selectedCategory}
                  orderType={orderType}
                  cartItems={cartItems}
                  setCartItems={setCartItems}
                />
              ) : (
                /* Other POS routes use Outlet */
                <Outlet context={{ 
                  selectedCategory, 
                  onCategorySelect: handleCategorySelect,
                  orderType,
                  onOrderTypeChange: handleOrderTypeChange,
                  cartItems,
                  setCartItems,
                  currentOrder,
                  setCurrentOrder
                }} />
              )}
            </div>
          </div>

          {/* Right Section - Current Order (only show on POS interface) */}
          {showPOSInterface && (
            <CurrentOrder
              cartItems={cartItems}
              setCartItems={setCartItems}
              orderType={orderType}
              branchId={user?.branchId}
              customerId={currentCustomerId} // Use dynamic customer ID
              onCustomerChange={handleCustomerIdChange} // Handle customer changes with logging
              deliveryProvider={orderType === 'delivery' ? deliveryProvider : null}
              selectedTable={selectedTable} // Pass selected table
              onTableClear={handleTableClear} // Pass table clear handler
              onOrderCalculationUpdate={setOrderCalculation} // Pass calculation update handler
              currentCustomer={currentCustomer}
              setCurrentCustomer={setCurrentCustomer}
              webPromotions={webPromotions}
              setWebPromotions={setWebPromotions}
              selectedWebPromo={selectedWebPromo}
              setSelectedWebPromo={setSelectedWebPromo}
            />
          )}
        </main>



        {/* Bottom Navigation - Only show on POS pages */}
        {showBottomNav && (
          <POSBottomNavigation
            orderType={orderType}
            onOrderTypeChange={handleOrderTypeChange}
            onTableSelect={handleTableSelect}
            selectedTable={selectedTable}
            userBranchId={user?.branchId} // Pass user's branch ID
            userRole={user?.role} // Pass user's role
            cartItems={cartItems}
            orderCalculation={orderCalculation}
            customerId={currentCustomerId} // Use dynamic customer ID
            onCustomerSelect={(customer) => {
              console.log('🔍 [POSLayout] User role being passed:', user?.role)
              console.log('🔍 [POSLayout] User branchId being passed:', user?.branchId)
              handleCustomerSelect(customer)
            }} // Pass customer selection handler
            onPaymentSuccess={handlePaymentSuccess}
            onClearAll={handleClearAll}
            onOrderLoad={handleOrderLoad} // Pass order load handler
            onPayLater={handlePayLater}
            onPayButtonClick={handlePayButtonClick} // Pass pay button handler that creates order first
            currentOrderId={currentOrderId} // Pass current order ID
            onOrderIdChange={setCurrentOrderId} // Pass order ID setter
            isPaymentModalOpen={isPaymentModalOpen}
            setIsPaymentModalOpen={setIsPaymentModalOpen}
            isHoldOrdersModalOpen={isHoldOrdersModalOpen}
            setIsHoldOrdersModalOpen={setIsHoldOrdersModalOpen}
            isPromotionsModalOpen={isPromotionsModalOpen}
            setIsPromotionsModalOpen={setIsPromotionsModalOpen}
            isVIPRoomsModalOpen={isVIPRoomsModalOpen}
            setIsVIPRoomsModalOpen={setIsVIPRoomsModalOpen}
            deliveryProvider={deliveryProvider} // Pass delivery provider
            deliveryReference={deliveryReference} // Pass delivery reference
            currentCustomer={currentCustomer}
            webPromotions={webPromotions}
            selectedWebPromo={selectedWebPromo}
            setSelectedWebPromo={setSelectedWebPromo}
          />
        )}
      </div>

      {/* Branch Selection Modal for admin/superadmin users - High z-index to appear in front */}
      <BranchSelectionModal
        isOpen={showBranchSelector}
        onClose={() => setShowBranchSelector(false)}
        onBranchSelect={handleBranchSelect}
        userRole={user?.role || ''}
        user={user}
      />

      {/* Delivery Provider Selection Popup */}
      {showDeliveryProviderPopup && (
        <DeliveryProviderPopup
          isOpen={showDeliveryProviderPopup}
          onClose={() => setShowDeliveryProviderPopup(false)}
          onProviderSelect={handleDeliveryProviderSelect}
        />
      )}

      {/* Promotions Modal - High z-index to appear in front of POS navigation */}
      <PromotionsModal
        isOpen={isPromotionsModalOpen}
        onClose={() => setIsPromotionsModalOpen(false)}
      />

      {/* Open Shift Modal - Only for Cashier and Barista roles */}
      {(user?.role === 'cashier' || user?.role === 'barista') && (
        <OpenShiftModal
          isOpen={isOpenShiftModalOpen}
          onClose={() => setIsOpenShiftModalOpen(false)}
          onConfirm={handleOpenShift}
          loading={shiftLoading}
        />
      )}

      {/* Close Shift Modal - Only for Cashier and Barista roles */}
      {(user?.role === 'cashier' || user?.role === 'barista') && (
        <CloseShiftModal
          isOpen={isCloseShiftModalOpen}
          onClose={() => setIsCloseShiftModalOpen(false)}
          onConfirm={handleCloseShift}
          openBalance={currentShift?.openBalance || 0}
          loading={shiftLoading}
        />
      )}

      {/* Barista Orders Modal */}
      <BaristaOrdersModal
        isOpen={isBaristaModalOpen}
        onClose={() => setIsBaristaModalOpen(false)}
        user={user}
      />

    </div>
  )
}

POSLayout.propTypes = {
  user: PropTypes.object.isRequired,
  onLogout: PropTypes.func.isRequired,
}